import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Sparkles, RefreshCw, TrendingUp, AlertTriangle, CheckCircle,
  Lightbulb, Users, Clock, Target, ArrowUp, ArrowDown, Minus,
} from 'lucide-react';

const r1 = v => Math.round(v * 10) / 10;

function generateInsightText(burnData, stats, utilData, deptData) {
  const projects = burnData?.projects ?? [];
  const overBurning = projects.filter(p => p.status === 'over_burning');
  const underBurning = projects.filter(p => p.status === 'under_burning');
  const exhausted = projects.filter(p => p.status === 'budget_exhausted');
  const onTrack = projects.filter(p => p.status === 'on_track');

  const totalHours = Math.round((utilData?.total_hours ?? 0) * 10) / 10;
  const billableHours = Math.round((utilData?.billable_hours ?? 0) * 10) / 10;
  const billableRatio = totalHours > 0 ? r1((billableHours / totalHours) * 100) : 0;

  const depts = deptData?.departments ?? [];

  const lines = [];

  lines.push('📊 Project Health Summary\n');
  lines.push(
    `Your team is running ${projects.length} active project${projects.length !== 1 ? 's' : ''} ` +
    `— ${exhausted.length + overBurning.length} at risk, ${onTrack.length} on track, ${underBurning.length} under-burning. ` +
    `Billable ratio this month: ${billableRatio}%.\n`
  );

  if (exhausted.length > 0) {
    lines.push('🚨 Budget Exhausted\n');
    exhausted.forEach(p => {
      lines.push(`• ${p.project_name} — ${Math.round((p.logged_hours || 0) * 10) / 10}h logged, 0h remaining. Immediate client discussion recommended.`);
    });
    lines.push('');
  }

  if (overBurning.length > 0) {
    lines.push('🔥 Over-Burning\n');
    overBurning.forEach(p => {
      const ahead = r1(p.burn_pct - p.time_elapsed_pct);
      lines.push(`• ${p.project_name} — ${p.burn_pct}% budget used vs ${p.time_elapsed_pct}% time elapsed (${ahead}% ahead). Burn rate: ${p.weekly_burn_rate}h/week, ${p.remaining_hours}h left.`);
    });
    lines.push('');
  }

  if (onTrack.length > 0) {
    lines.push('✅ On Track\n');
    onTrack.slice(0, 5).forEach(p => {
      lines.push(`• ${p.project_name} — ${p.burn_pct}% burned, ${p.remaining_hours}h remaining.`);
    });
    if (onTrack.length > 5) lines.push(`  …and ${onTrack.length - 5} more on-track projects.`);
    lines.push('');
  }

  if (underBurning.length > 0) {
    lines.push('⚠️ Under-Burning\n');
    lines.push(`${underBurning.length} project${underBurning.length !== 1 ? 's are' : ' is'} significantly behind expected burn — may indicate blocked work, delayed kick-offs, or under-reporting:`);
    underBurning.slice(0, 4).forEach(p => {
      lines.push(`• ${p.project_name} — ${p.burn_pct}% burned vs ${p.time_elapsed_pct}% time elapsed.`);
    });
    if (underBurning.length > 4) lines.push(`  …and ${underBurning.length - 4} more.`);
    lines.push('');
  }

  if (depts.length > 0) {
    lines.push('👥 Department Activity\n');
    [...depts].sort((a, b) => b.total - a.total).forEach(d => {
      const dBillable = d.total > 0 ? Math.round((d.billable / d.total) * 100) : 0;
      lines.push(`• ${d.department.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: ${d.total}h total, ${dBillable}% billable`);
    });
    lines.push('');
  }

  lines.push('⚡ Recommendations\n');
  let recN = 1;
  if (exhausted.length > 0) lines.push(`${recN++}. Escalate budget overrun on ${exhausted.slice(0, 3).map(p => p.project_name).join(', ')}${exhausted.length > 3 ? ` +${exhausted.length - 3} more` : ''} to clients immediately.`);
  if (overBurning.length > 0) lines.push(`${recN++}. Review scope on over-burning projects — reallocate team members or renegotiate timelines.`);
  if (underBurning.length > 0) lines.push(`${recN++}. Hold a blocker check-in on under-burning projects to surface unreported delays.`);
  if (billableRatio < 70) lines.push(`${recN++}. Billable ratio is below 70% — audit non-billable time and reduce internal overhead where possible.`);
  else if (billableRatio >= 80) lines.push(`${recN++}. Billable ratio of ${billableRatio}% is strong — maintain current allocation mix.`);
  if (recN === 1) lines.push('• All projects are on track. Continue current pace and review allocations next sprint.');

  return lines.join('\n');
}

const AIInsightsPage = () => {
  const { api, canViewReports } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const [burnRes, statsRes, utilRes, deptRes] = await Promise.allSettled([
        api.get('/dashboard/burn-rate'),
        api.get('/dashboard/stats'),
        api.get('/dashboard/utilization?period=month'),
        api.get('/dashboard/department-stats'),
      ]);

      const burnData  = burnRes.status  === 'fulfilled' ? burnRes.value.data  : null;
      const statsData = statsRes.status === 'fulfilled' ? statsRes.value.data : null;
      const utilData  = utilRes.status  === 'fulfilled' ? utilRes.value.data  : null;
      const deptData  = deptRes.status  === 'fulfilled' ? deptRes.value.data  : null;

      const projects = burnData?.projects ?? [];
      const totalHours = Math.round((utilData?.total_hours ?? 0) * 10) / 10;
      const billableHours = Math.round((utilData?.billable_hours ?? 0) * 10) / 10;
      const billableRatio = totalHours > 0 ? Math.round((billableHours / totalHours) * 1000) / 10 : 0;

      const depts = deptData?.departments ?? [];

      const trends = [
        {
          label: 'this month',
          value: `${billableRatio}%`,
          direction: billableRatio >= 75 ? 'up' : 'down',
          description: 'Billable ratio',
        },
        {
          label: 'this month',
          value: `${totalHours}h`,
          direction: 'neutral',
          description: 'Total hours logged',
        },
        {
          label: 'active',
          value: `${projects.filter(p => p.status === 'over_burning' || p.status === 'budget_exhausted').length}`,
          direction: projects.filter(p => p.status === 'over_burning' || p.status === 'budget_exhausted').length > 0 ? 'down' : 'up',
          description: 'At-risk projects',
        },
        {
          label: 'pending',
          value: `${statsData?.pending_approvals ?? 0}`,
          direction: (statsData?.pending_approvals ?? 0) > 5 ? 'down' : 'neutral',
          description: 'Unapproved timesheets',
        },
      ];

      setData({
        metrics: {
          total_hours: totalHours,
          billable_hours: billableHours,
          billable_ratio: billableRatio,
          active_projects: statsData?.active_projects ?? projects.length,
          department_hours: Object.fromEntries(depts.map(d => [d.department, d.total])),
          project_utilization: projects.map(p => ({
            name: p.project_name,
            utilization: p.burn_pct,
            remaining: p.remaining_hours,
            status: p.status,
          })),
        },
        trends,
        insights: generateInsightText(burnData, statsData, utilData, deptData),
      });
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  if (!canViewReports) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold">Access Restricted</h2>
          <p className="text-muted-foreground mt-2">You don't have permission to view AI insights</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ai-insights-page">
      {/* Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-blue-600/10 via-indigo-500/5 to-violet-400/10 p-6">
        <div className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full bg-primary/5 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-primary/5 blur-2xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-background/70 backdrop-blur-sm border border-border flex items-center justify-center shadow-sm">
              <Sparkles className="w-7 h-7 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">AI Powered</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                <span className="bg-gradient-to-r from-primary to-violet-500 bg-clip-text text-transparent">AI Insights</span>
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-primary/60" />
                AI-powered analysis of your live project and time tracking data
              </p>
            </div>
          </div>
          <Button onClick={fetchInsights} disabled={loading} className="gap-2 bg-background/70 backdrop-blur-sm flex-shrink-0" variant="outline" data-testid="refresh-insights-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Insights
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Analyzing your projects...</p>
          </div>
        </div>
      ) : !data ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold">No data available</h2>
            <p className="text-muted-foreground mt-2">Log time entries and create projects to generate insights</p>
          </div>
        </div>
      ) : (
        <>
          {/* Metric Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-tour="ai-metric-cards">
            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center mb-3">
                  <Clock className="w-5 h-5 text-accent" />
                </div>
                <div className="text-2xl font-bold">{data.metrics.total_hours}h</div>
                <div className="text-sm text-muted-foreground">Total Hours (Month)</div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center mb-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div className="text-2xl font-bold">{data.metrics.billable_hours}h</div>
                <div className="text-sm text-muted-foreground">Billable Hours</div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mb-3">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                </div>
                <div className="text-2xl font-bold">{data.metrics.billable_ratio}%</div>
                <div className="text-sm text-muted-foreground">Billable Ratio</div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center mb-3">
                  <Target className="w-5 h-5 text-purple-500" />
                </div>
                <div className="text-2xl font-bold">{data.metrics.active_projects}</div>
                <div className="text-sm text-muted-foreground">Active Projects</div>
              </CardContent>
            </Card>
          </div>

          {/* Trend Pills */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-tour="ai-trends">
            {data.trends.map((t, i) => (
              <Card key={i} className="card-hover">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    t.direction === 'up' ? 'bg-green-500/10' : t.direction === 'down' ? 'bg-red-500/10' : 'bg-muted'
                  }`}>
                    {t.direction === 'up' ? <ArrowUp className="w-4 h-4 text-green-500" /> :
                     t.direction === 'down' ? <ArrowDown className="w-4 h-4 text-red-500" /> :
                     <Minus className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <div className="min-w-0">
                    <div className={`text-lg font-bold truncate ${
                      t.direction === 'up' ? 'text-green-600 dark:text-green-400' :
                      t.direction === 'down' ? 'text-red-600 dark:text-red-400' : ''
                    }`}>{t.value}</div>
                    <div className="text-xs text-muted-foreground leading-tight">{t.description}<br />{t.label}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* AI Analysis */}
          <Card className="ai-insight-container" data-testid="ai-analysis-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lightbulb className="w-5 h-5 text-accent" />
                AI Analysis &amp; Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="whitespace-pre-wrap text-foreground leading-relaxed text-sm">
                {data.insights}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Department Breakdown */}
            {Object.keys(data.metrics.department_hours).length > 0 && (
              <Card data-testid="department-breakdown">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="w-5 h-5" /> Department Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(data.metrics.department_hours)
                      .sort((a, b) => b[1] - a[1])
                      .map(([dept, hours]) => {
                        const total = Object.values(data.metrics.department_hours).reduce((s, h) => s + h, 0);
                        const pct = total > 0 ? Math.round((hours / total) * 100) : 0;
                        return (
                          <div key={dept}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium capitalize">{dept.replace(/_/g, ' ')}</span>
                              <span className="text-muted-foreground font-mono">{hours}h · {pct}%</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-accent rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Project Utilization — burning only */}
            {data.metrics.project_utilization.filter(p => p.status === 'over_burning' || p.status === 'budget_exhausted').length > 0 && (
              <Card data-testid="project-utilization">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <AlertTriangle className="w-5 h-5 text-orange-500" /> Over-Burning Projects
                    <span className="ml-auto text-xs font-normal text-muted-foreground">
                      {data.metrics.project_utilization.filter(p => p.status === 'over_burning' || p.status === 'budget_exhausted').length} at risk
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {data.metrics.project_utilization
                      .filter(p => p.status === 'over_burning' || p.status === 'budget_exhausted')
                      .sort((a, b) => b.utilization - a.utilization)
                      .map((project, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between mb-1">
                              <span className="text-sm font-medium truncate">{project.name}</span>
                              <span className="text-sm font-mono ml-2 flex-shrink-0">{project.utilization}%</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  project.utilization >= 100 ? 'bg-red-500' :
                                  project.utilization > 80  ? 'bg-orange-500' :
                                  'bg-accent'
                                }`}
                                style={{ width: `${Math.min(project.utilization, 100)}%` }}
                              />
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground w-20 text-right flex-shrink-0">
                            {project.remaining > 0 ? `${project.remaining}h left` : 'Over budget'}
                          </div>
                          {project.utilization > 90 && (
                            <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${project.utilization >= 100 ? 'text-red-500' : 'text-orange-500'}`} />
                          )}
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AIInsightsPage;
